import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Achievement } from '../utils/achievementManager';

interface AchievementNotificationProps {
  achievement: Achievement | null;
  onClose: () => void;
}

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const NotificationContainer = styled.div<{ $isVisible: boolean; $rarity: string }>`
  position: fixed;
  bottom: max(20px, env(safe-area-inset-bottom, 20px));
  left: 50%;
  transform: translateX(-50%);
  background: ${props => {
    switch (props.$rarity) {
      case 'legendary': return 'linear-gradient(135deg, #FFD700, #FFA500, #FF8C00)';
      case 'epic': return 'linear-gradient(135deg, #9C27B0, #673AB7, #3F51B5)';
      case 'rare': return 'linear-gradient(135deg, #2196F3, #03A9F4, #00BCD4)';
      default: return 'linear-gradient(135deg, #4CAF50, #8BC34A, #CDDC39)';
    }
  }};
  color: white;
  padding: 12px 16px;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  min-width: 280px;
  max-width: 90%;
  z-index: 1300;
  animation: ${props => props.$isVisible ? slideIn : slideOut} 0.5s ease-out;
  border: 2px solid ${props => {
    switch (props.$rarity) {
      case 'legendary': return '#FFD700';
      case 'epic': return '#E1BEE7';
      case 'rare': return '#BBDEFB';
      default: return '#C8E6C9';
    }
  }};
  
  @media (max-width: 480px) {
    min-width: 260px;
    padding: 10px 14px;
    font-size: 13px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.95;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Icon = styled.div`
  font-size: 32px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
`;

const TextContent = styled.div`
  flex: 1;
`;

const AchievementName = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 4px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const AchievementDescription = styled.div`
  font-size: 14px;
  opacity: 0.9;
  line-height: 1.3;
`;

const RarityBadge = styled.div<{ $rarity: string }>`
  position: absolute;
  top: -8px;
  left: -8px;
  background: ${props => {
    switch (props.$rarity) {
      case 'legendary': return '#FFD700';
      case 'epic': return '#9C27B0';
      case 'rare': return '#2196F3';
      default: return '#4CAF50';
    }
  }};
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 8px;
  text-transform: uppercase;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const AchievementNotification: React.FC<AchievementNotificationProps> = ({ 
  achievement, 
  onClose 
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = React.useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 500); // Vänta på animation
  }, [onClose]);

  useEffect(() => {
    if (!achievement) return;
    setIsVisible(true);
    // Auto-close efter 5 sekunder
    const timer = setTimeout(() => {
      handleClose();
    }, 5000);
    return () => clearTimeout(timer);
  }, [achievement, handleClose]);

  if (!achievement) return null;

  const getRarityText = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'Legendarisk';
      case 'epic': return 'Episk';
      case 'rare': return 'Sällsynt';
      default: return 'Vanlig';
    }
  };

  return (
    <NotificationContainer 
      $isVisible={isVisible} 
      $rarity={achievement.rarity}
    >
      <RarityBadge $rarity={achievement.rarity}>
        {getRarityText(achievement.rarity)}
      </RarityBadge>
      
      <Header>
        <Title>🏆 Prestation Upplåst!</Title>
        <CloseButton onClick={handleClose}>×</CloseButton>
      </Header>
      
      <Content>
        <Icon>{achievement.icon}</Icon>
        <TextContent>
          <AchievementName>{achievement.name}</AchievementName>
          <AchievementDescription>{achievement.description}</AchievementDescription>
        </TextContent>
      </Content>
    </NotificationContainer>
  );
};

export default AchievementNotification;
